import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Avatar,
  useTheme,
  Skeleton
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';

import { IcmsPorNcm } from './types';

type TableIcmsNcmProps = {
  data: IcmsPorNcm[];
  title: string;
  loading?: boolean;
};

const formatCurrency = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const TableIcmsNcm: React.FC<TableIcmsNcmProps> = ({ data, title, loading = false }) => {
  const theme = useTheme();

  return (
    <Card sx={{ borderRadius: 3, p: 2 }}>
      <CardContent sx={{ p: 0 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            sx={{
              bgcolor: theme.palette.error.light,
              color: theme.palette.error.main,
              width: 36,
              height: 36,
              mr: 1
            }}
          >
            <BalanceIcon />
          </Avatar>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
            {title}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell><strong>Descrição Completa</strong></TableCell>
                <TableCell align="right"><strong>Crédito ICMS</strong></TableCell>
                <TableCell align="right"><strong>Débito ICMS</strong></TableCell>
                <TableCell align="right"><strong>Saldo ICMS</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow
                    key={`skeleton-${index}`}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5'
                    }}
                  >
                    <TableCell>
                      <Skeleton variant="text" width="80%" height={16} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="90%" height={16} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width="70%" height={16} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width="70%" height={16} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" width="70%" height={16} />
                    </TableCell>
                  </TableRow>
                ))
              ) : data.length > 0 ? (
                data.map((item, index) => (
                  <TableRow
                    key={`${item.ncm}-${index}`}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5'
                    }}
                  >
                    <TableCell>{item.ncm}</TableCell>
                    <TableCell>{item.descricao}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={400} color={theme.palette.success.main}>
                        {formatCurrency(item.credito_icms)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={400} color={theme.palette.error.main}>
                        {formatCurrency(item.debito_icms)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight={400}
                        color={
                          (item.saldo_icms ?? 0) > 0
                            ? theme.palette.error.main
                            : theme.palette.success.main
                        }
                      >
                        {formatCurrency(item.saldo_icms)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum dado encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default TableIcmsNcm;
